import { mdiClose, mdiFileDocument, mdiFilePdfBox, mdiFilePlusOutline, mdiImage, mdiMusicBox } from "@mdi/js";
import Icon from "@mdi/react";
import { Button, Dialog, Grid, TextField, Tooltip, Typography } from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import React, { useEffect, useState } from "react";
import { useSnackbar } from "../../components/context/SnackbarContext";
import useAxios from "../../hooks/useAxios";
import { useLoader } from "../../components/context/LoaderContext";
import { Formik } from "formik";
import * as yup from "yup";
import moment from "moment-timezone";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useSelector } from "react-redux";
import { PickerWrapper } from "../../utils/Utils";
import DynamicFields from "./CustomFields";


function AddVisitLogDialog({ onClose, open, type, dataId, handleReloadFunc, editId }) {
    const axiosInstance = useAxios();
    const { showSnackbar } = useSnackbar();
    const { showLoader, hideLoader } = useLoader();
    const [logVisitList, setLogVisitList] = useState([])
    const { preferredDateFormat, preTimeZoneKey } = useSelector((state) => state?.user);

    const [selectedFiles, setSelectedFiles] = useState([]);
    const [customFields, setCustomFields] = useState([]);

    //=======Start::Write a code for manage Edit Notes======//
    useEffect(() => {
        if (editId) {
            const fetchData = async () => {
                await getLogData();
            };
            fetchData();
        } else {
            async function fetchData() {
                getData();
            }
            fetchData();
        }
    }, []);


    const getData = async () => {
        showLoader();
        await axiosInstance.get(`api/${type}-log-visit/create/?${type}Id=${dataId}`).then((response) => {
            if (response.status === 200) {
                setCustomFields(response?.data?.data?.customFields || []);
                hideLoader();
            }
        })
            .then((json) => { })
            .catch((error) => {
                if (error.response) {
                    hideLoader();
                    if (error.response.status === 400) {
                        showSnackbar(error.response.data.message, "warning");
                    }
                }
            });
    }


    async function getLogData() {
        showLoader()
        try {
            const response = await axiosInstance.get(`api/${type}-log-visit/edit/${editId}?${type}Id=${dataId}&type=${type}`);
            if (response.data.code === 200) {
                setLogVisitList(response.data.data.record);
                setCustomFields(response?.data?.data?.customFields || []);
            }
        } catch (error) {
            if (error.response) {
                showSnackbar(error.response.data.message, "warning");
            }
        } finally {
            hideLoader();
        }
    }
    //=======Start::Write a code for manage Edit Notes======//

    const iconMapping = {
        image: mdiImage,
        audio: mdiMusicBox,
        file: mdiFileDocument,
        pdf: mdiFilePdfBox,
        // Add more mappings as needed
    };

    const allowedMimeTypes = [
        'image/jpeg', 'image/png', 'image/gif',
        'video/mp4', 'video/quicktime', 'video/x-msvideo',
        'video/x-ms-wmv', 'video/x-matroska', 'audio/mpeg',
        'audio/ogg', 'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/vnd.oasis.opendocument.spreadsheet', 'application/vnd.oasis.opendocument.text',
        'application/vnd.oasis.opendocument.presentation', 'text/csv', 'application/pdf',
        'text/plain', 'application/vnd.ms-office', 'text/x-c', 'text/anytext', 'application/ogg', 'application/x-ogg',
        'text/comma-separated-values', 'application/x-ogg', 'text/anytext'
    ];

    const buildInitialValues = (customFields) => {
        // Default initial values for standard fields
        const initial = {
            name: logVisitList?.name || '',
            description: logVisitList?.description || '',
            end_date: logVisitList?.end_date ? moment(logVisitList?.end_date) : moment(),
            attachment: [],
            error_list: [],
        };

        // Step 1: Map custom field values from the eventList
        const customValuesMap = {};

        if (Array.isArray(logVisitList?.custom_field_values)) {
            logVisitList.custom_field_values.forEach(item => {
                if (item?.field_name) {
                    customValuesMap[item.field_name.trim()] = item.value;
                }
            });
        }

        // Step 2: Initialize values for all custom fields
        customFields.forEach(field => {
            const key = field.field_name?.trim();
            const fieldValue = customValuesMap[key];

            switch (field.display_type) {
                case 'checkbox':
                    try {
                        // If it's a stored JSON string like '["a", "b"]'
                        initial[key] = fieldValue ? JSON.parse(fieldValue) : [];
                    } catch {
                        initial[key] = Array.isArray(fieldValue) ? fieldValue : [];
                    }
                    break;

                case 'file':
                    // You can customize this if you support showing uploaded file names
                    initial[key] = []; // Typically empty unless you want to preview old uploads
                    break;

                case 'date':
                case 'datetime':
                    initial[key] = fieldValue ? moment(fieldValue) : '';
                    break;

                default:
                    initial[key] = fieldValue ?? '';
                    break;
            }
        });

        return initial;
    };


    const buildValidationSchema = (customFields) => {
        const schema = {
            name: yup
                .string()
                .required("Enter event subject")
                .min(2, "The name must be at least 2 characters.")
                .max(50, "The name must not be greater than 50 characters."),
            description: yup
                .string()
                .required("Enter event description")
                .min(2, "The description must be at least 2 characters.")
                .max(150, "The description must not be greater than 150 characters."),
            end_date: yup
                .string()
                .required("Enter Visit date"),
            file_url: yup
                .array()
                .nullable()
                .of(
                    yup
                        .mixed()
                        .test('fileFormat', 'Unsupported file format. Please upload a valid file type.', (value) => {
                            return value && allowedMimeTypes.includes(value.type);
                        })
                        .test('fileSize', 'File size exceeds 4 MB', (value) => {
                            return value && value.size <= 4 * 1024 * 1024;
                        })
                )
                //.required('Please select at least one file')
                .test('validFiles', 'Files are not uploaded due to validation errors', function (value) {
                    if (value) {
                        return value.every(file => {
                            const isValidFormat = allowedMimeTypes.includes(file.type);
                            const isValidSize = file.size <= 4 * 1024 * 1024;
                            return isValidFormat && isValidSize;
                        });
                    }
                    return true;
                }),
        };

        customFields.forEach(field => {
            const key = field?.field_name;
            const label = field?.label || field?.field_name;
            const isRequired = field?.is_required === 'yes';

            switch (field?.display_type) {
                case 'text':
                case 'textarea':
                    schema[key] = yup.string();
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;

                case 'email':
                    schema[key] = yup.string().email('Enter a valid email');
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;

                case 'phone':
                    schema[key] = yup
                        .string()
                        .matches(/^[0-9()+-\s]*$/, 'Enter a valid phone number');
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;

                case 'number':
                    schema[key] = yup
                        .number()
                        .typeError(`${label} must be a number`);
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;

                case 'select':
                    if (field?.is_multiple === 'yes') {
                        // MULTI-SELECT
                        schema[key] = yup.array().of(yup.string());
                        if (isRequired) {
                            schema[key] = schema[key].min(1, `Select at least one ${label}`);
                        }
                    } else {
                        // SINGLE SELECT
                        schema[key] = yup.string();
                        if (isRequired) {
                            schema[key] = schema[key].required(`${label} is required`);
                        }
                    }
                    break;
                case 'radio':
                    schema[key] = yup.string();
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;

                case 'checkbox':
                    schema[key] = yup.array();
                    if (isRequired) schema[key] = schema[key].min(1, `Select at least one ${label}`);
                    break;

                case 'date':
                case 'datetime':
                    schema[key] = yup.string();
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;

                case 'file':
                    schema[key] = yup.array().of(
                        yup
                            .mixed()
                            .test('fileSize', 'File too large', value => !value || value.size <= 4 * 1024 * 1024)
                    );
                    if (isRequired) {
                        schema[key] = schema[key].min(1, `Upload at least one ${label}`);
                    }
                    break;

                default:
                    schema[key] = yup.string();
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;
            }
        });

        return yup.object().shape(schema);
    };

    const staticFields = ['id', 'leadId', 'dealId', 'name', 'description', 'activity_type', 'activity_attachment_type', 'end_date', 'attachment'];


    const handleFileChange = (event) => {
        const files = Array.from(event.currentTarget.files);
        const errors = [];
        const validFiles = [];

        const allowedMimeTypes = [
            'image/jpeg', 'image/png', 'image/gif', 'video/mp4', 'video/quicktime',
            'video/x-msvideo', 'video/x-ms-wmv', 'video/x-matroska', 'audio/mpeg',
            'audio/ogg', 'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            'application/vnd.oasis.opendocument.spreadsheet', 'application/vnd.oasis.opendocument.text',
            'application/vnd.oasis.opendocument.presentation', 'text/csv', 'application/pdf',
            'text/plain', 'application/vnd.ms-office', 'text/x-c', 'text/anytext',
            'application/ogg', 'application/x-ogg', 'text/comma-separated-values',
            'application/x-ogg', 'text/anytext'
        ];

        files.forEach((file) => {
            if (file.size > 4 * 1024 * 1024) {
                //errors.push(`${file.name} exceeds 4MB size limit`);
            } else if (!allowedMimeTypes.includes(file.type)) {
                // errors.push(`${file.name} is an invalid file type`);
            } else {
                validFiles.push(file);
            }
        });

        if (errors.length > 0) {
            showSnackbar(`${errors.length} file(s) not uploaded due to validation errors`, 'error');
        } else {
            //showSnackbar(`${validFiles.length} file(s) are added successfully`, 'success');
        }

        setSelectedFiles([...selectedFiles, ...validFiles]);
        //setFileErrors(errors);
        event.target.value = ''
    };

    const removeFile = (index, setFieldValue, errors) => {
        // Remove file from selectedFiles array
        const newFiles = [...selectedFiles];
        newFiles.splice(index, 1);
        setSelectedFiles(newFiles);

        // Remove the corresponding error from errors.file_url
        //const newErrors = [...errors.file_url];
        const newErrors = Array.isArray(errors.file_url) ? [...errors.file_url] : [];
        newErrors.splice(index, 1);

        // Update Formik's field value
        setFieldValue("file_url", newErrors);
    };

    const handleSubmit = async (values, onSubmitProps) => {
        const formData = new FormData();
        const customFieldsData = {};

        if (logVisitList?.id) {
            formData.append('id', logVisitList?.id)
        }
        formData.append('leadId', dataId)
        formData.append('dealId', dataId)
        formData.append('type', type)
        formData.append('name', values?.name || '')
        formData.append('description', values?.description || '')
        formData.append('activity_type', 'Log a Visit')
        formData.append('activity_attachment_type', 'Attachment')
        formData.append('end_date', values.end_date
            ? moment(values.end_date).format("YYYY-MM-DD HH:mm:ss")
            : moment(new Date()).format("YYYY-MM-DD HH:mm:ss"))

        for (var i = 0; i < selectedFiles.length; i++) {
            formData.append('attachment[]', selectedFiles[i])
        }


        Object.entries(values).forEach(([key, value]) => {
            if (!staticFields.includes(key)) {
                const isFileArray = Array.isArray(value) && value.length && value[0] instanceof File;

                if (isFileArray) {
                    value.forEach((file, idx) => {
                        //formData.append(`${key}[${idx}]`, file);
                        formData.append(`${key}[]`, file);
                    });
                } else {
                    customFieldsData[key] = value;
                }
            }
        });

        // Append custom fields JSON string under one key
        formData.append('custom_fields', JSON.stringify(customFieldsData));

        try {
            showLoader();
            const response = await axiosInstance.post(`/api/${type}-log-visit/${editId ? 'update' : 'store'}`, formData);
            if (response.status === 200) {
                showSnackbar(response.data.message, 'success');
                onClose();
                handleReloadFunc();
            }
        } catch (error) {
            hideLoader();
            if (error.response) {
                if (error.response.status === 422 || error.response.data.validation_errors) {
                    const errors = error.response.data.validation_errors;
                    Object.keys(errors).forEach(key => {
                        onSubmitProps.setFieldError(key, errors[key]);
                    });
                } else {
                    showSnackbar(error.response.data.message, 'error');
                }
            } else {
                // Handle cases where error.response is undefined (e.g., network errors)
                showSnackbar('An unexpected error occurred. Please try again.', 'error');
            }
        } finally {
            hideLoader();
        }
    }

    return (
        <>
            <Dialog onClose={onClose} open={open}>
                <div className="dialog-title">
                    <h2>{editId ? 'Edit' : 'Add'} Visit Log</h2>
                    <Tooltip title="Close">
                        <button className="icon-button dialog-close" onClick={onClose}>
                            <Icon path={mdiClose} color={'currentColor'} size={1} />
                        </button>
                    </Tooltip>
                </div>
                <Formik
                    initialValues={buildInitialValues(customFields)}
                    validationSchema={buildValidationSchema(customFields)}
                    onSubmit={handleSubmit}
                    //enableReinitialize={editId ? true : false}
                    enableReinitialize
                >
                    {props => {
                        const {
                            touched,
                            errors,
                            values,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            setFieldValue
                        } = props;
                        return (
                            <form onSubmit={handleSubmit}>
                                <TextField
                                    label="Subject"
                                    variant="filled"
                                    name="name"
                                    value={values?.name || ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.name && touched.name}
                                    helperText={(errors.name && touched.name) && errors.name}
                                />

                                <TextField
                                    label="Description"
                                    variant="filled"
                                    name="description"
                                    value={values?.description || ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    multiline
                                    rows={3}
                                    error={errors.description && touched.description}
                                    helperText={(errors.description && touched.description) && errors.description}
                                />


                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DateTimePicker
                                        label="Visit Date"
                                        format={preferredDateFormat}
                                        views={["day", "hours", "minutes", "month", "year"]}
                                        name="end_date"
                                        slots={{
                                            popper: PickerWrapper
                                        }}
                                        slotProps={{
                                            textField: {
                                                variant: 'filled',
                                                name: "end_date",
                                                type: 'text',
                                                error: errors.end_date && touched.end_date,
                                                helperText: (errors.end_date && touched.end_date) && errors.end_date
                                            }
                                        }}
                                        value={values?.end_date ? moment.utc(values.end_date).tz(preTimeZoneKey) : moment()}
                                        onChange={(value) => setFieldValue("end_date", value, true)}
                                        onBlur={handleBlur}
                                    />

                                </LocalizationProvider>

                                <Grid item xs={12}>
                                    <div className="attachment-center">
                                        <div className="title">
                                            <Typography variant="title-medium-bold">Attachments</Typography> <Typography variant="title-small" className="file-size">(Max Size 4 MB)</Typography>
                                        </div>

                                        <div className="psuedo-upload-block">
                                            <div className="content">
                                                <Button color="secondary" className="btn-block" tabIndex={-1}>
                                                    <span>
                                                        <Icon path={mdiFilePlusOutline} color={'currentColor'} size={1} />
                                                        <span>Upload File</span>
                                                    </span>
                                                </Button>
                                            </div>
                                            <input
                                                type="file"
                                                multiple
                                                name="file_url"
                                                className="psuedo-upload-element"
                                                onChange={(event) => {
                                                    handleFileChange(event);
                                                    setFieldValue("file_url", Array.from(event.currentTarget.files));
                                                }}
                                                onBlur={handleBlur}
                                                accept=".jpeg,.png,.jpg,.gif,.svg,.pdf,.mp4,.mp3,.3gp,.avi,.xls,.xlsx,.doc,.docx,.ppt,.pptx,.ods,.odt,.odp,.csv,.txt,.application/msword,.application/vnd.ms-powerpoint,.application/vnd.oasis.opendocument.text,.application/vnd.ms-excel,.application/vnd.openxmlformats-officedocument.wordprocessingml.document,.application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.application/vnd.openxmlformats-officedocument.presentationml.presentation"
                                                title="Allow file type are jpeg,png,jpg,gif,svg,pdf,mp4,mp3,3gpp,x-msvideo,xls,xlsx,doc,docx,ppt,pptx,ods,odt,odp,csv,anytext,plain,ms-office and MAX each file size are 4MB"
                                            />


                                        </div>
                                    </div>
                                </Grid>
                                {selectedFiles && selectedFiles.length > 0 && <Grid container component={'ul'} spacing={1.5} className="attachments">
                                    {selectedFiles.map((attachment, index) => {
                                        const fileExtension = attachment && attachment?.name?.split('.').pop().toLowerCase();

                                        let fileType = '';
                                        if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension)) {
                                            fileType = 'image';
                                        } else if (['mp3', 'wav', 'ogg', 'mp4'].includes(fileExtension)) {
                                            fileType = 'audio';
                                        } else if (['pdf'].includes(fileExtension)) {
                                            fileType = 'pdf';
                                        } else {
                                            fileType = 'file';
                                        }
                                        const IconComponent = iconMapping[fileType];
                                        return (
                                            <Grid item component={'li'} xs={12} key={index + ''}>
                                                <a href={attachment.fileUrl} className={(Array.isArray(errors.file_url) && errors.file_url.length > 0 && errors.file_url[index]) ? 'has-error' : ''}>
                                                    <div className="icon-wrapper">
                                                        {IconComponent && <Icon path={IconComponent} color={'currentColor'} size={1} />}
                                                    </div>
                                                    <div className="info">
                                                        <Typography variant="body-large" component={'p'}>{attachment.name}</Typography>
                                                        {attachment.size && <Typography variant="body-medium">Size: {(attachment?.size / 1024 / 1024).toFixed(2)} MB</Typography>}
                                                    </div>
                                                </a>
                                                {selectedFiles && <Tooltip title="Remove">
                                                    <button className="icon-button size-small" onClick={() => removeFile(index, setFieldValue, errors)}>
                                                        <Icon path={mdiClose} color={'currentColor'} size={1} />
                                                    </button>
                                                </Tooltip>}
                                                {Array.isArray(errors.file_url) && errors.file_url[index] && (
                                                    <ul className='error-text'>
                                                        {Array.isArray(errors.file_url[index]) ? errors.file_url[index].map((error, idx) => (
                                                            <li key={idx}>{error}</li>
                                                        )) : <li>{errors.file_url[index]}</li>}
                                                    </ul>
                                                )}
                                            </Grid>
                                        )
                                    }
                                    )}
                                </Grid>}
                                {customFields?.length > 0 &&
                                    <Typography variant="title-medium-large-semibold" component={'h4'} className="mb-12">Custom Fields</Typography>
                                }
                                {customFields?.length > 0 &&
                                    customFields.map((field, idx) => (
                                        <DynamicFields
                                            key={idx}
                                            field={field}
                                            values={values}
                                            handleChange={handleChange}
                                            handleBlur={handleBlur}
                                            errors={errors}
                                            touched={touched}
                                            setFieldValue={setFieldValue}
                                            selectedFiles={editId ? logVisitList?.custom_field_values : []}
                                        />
                                    ))
                                }

                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Button className="btn-block" type="submit">{editId ? 'Edit' : 'Add'} Visit Log</Button>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Button color="secondary" className="btn-block" onClick={onClose}>Cancel</Button>
                                    </Grid>
                                </Grid>
                            </form>
                        );
                    }}
                </Formik>
            </Dialog>
        </>
    );
}

export default AddVisitLogDialog;